import { customElement, property, state, query } from "lit/decorators.js";
import { html, LitElement, nothing, PropertyValues, TemplateResult } from "lit";
import { Translatable } from "@slhuinav/i18n/src/mixins/translatable";
import { repeat } from "lit/directives/repeat.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { ApplicationItemInterface, UserInterface } from "@slhuinav/base";
import {
  IconDefaultType,
  ICON_FAMILY_NAVIGATION,
  ICON_FAMILY_DEFAULT,
} from "@slhuiicon/icon-core";
import { Sizes, UserType } from "@slhuinav/avatar/src/avatar.type";
import { SlhNavigationItemTypeEnum } from "@slhuinav/item/src/item.type";
import { ITEM_CLICK } from "@slhuinav/item";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import { SlhNavigationList } from "@slhuinav/list";
import {
  SIDEBAR_ACTIVATOR_EVENT_CLICK,
  NAVIGATION_LIST_OFFSET,
  ACTIVE_ORGANIZATION_ICON,
  ORGANIZATION_VISIBLE_ITEMS,
  ORGANIZATION_TITLE_MAX_CHARACTER,
  A11Y_OPEN_SIDEBAR_KEYCODES,
  EVENT_SIDEBAR_CLOSED,
  CART_CHANGED_EVENT,
  DECIMAL_PLACES,
  SIGN_IN_ICON,
} from "./headerbar.constants";
import { AvatarInterface, CartInterface } from "./headerbar.interface";
import { style } from "./assets/headerbar.generated-style";

/**
 * @customElement slh-nav-headerbar
 *
 * @cssprop --slh-color-neutral - Headerbar background color. Primary link font color. Headerbar user applications full name color.
 * @cssprop --slh-color-neutral-shade-00 - Headerbar font color. Icon hover color. Navigation font color. Link font color.
 * @cssprop --slh-color-neutral-shade-20 - Headerbar user applications separator color.
 * @cssprop --slh-color-neutral-shade-40 - Headerbar icons color.
 * @cssprop --slh-color-neutral-shade-60 - Headerbar user applications items color
 * @cssprop --slh-color-neutral-shade-80 - Headerbar icons hover holder background color. Primary link font color.
 * @cssprop --slh-base-m-font-size - Link font size.
 * @cssprop ---slh-base-xs-font-size - Headerbar font size. Headerbar user profile font size.
 * @cssprop --slh-default-font-family - Navigation font family.
 * @cssprop --slh-base-semi-bold-font-weight - Link font weight.
 * @cssprop --slh-base-bold-font-weight - Navigation font weight. Headerbar user profile font weight.
 * @cssprop --slh-base-xxs-line-height - Navigation line height.
 * @cssprop --slh-color-neutral-shade-80-opacity-18 - Link focus shadow color.
 * @cssprop --slh-color-neutral-shade-10 - Link hover font color.
 * @cssprop --slh-color-primary-shade-40 - Primary link background color.
 * @cssprop --slh-color-primary-shade-60 - Primary link hover background color.
 * @cssprop --slh-color-outline - Link outline color.
 */
@customElement("slh-nav-headerbar")
export default class SlhNavigationHeaderbar extends Translatable(LitElement) {
  static styles = style;

  /**
   * User data
   */
  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  user?: UserInterface = undefined;

  /**
   * User application items
   */
  @property({
    type: Array,
    attribute: "user-applications",
    reflect: true,
  })
  userApplications: Array<ApplicationItemInterface> = [];

  /**
   * Settings items
   */
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  settings: Array<ApplicationItemInterface> = [];

  /**
   * Sign Out url and title
   */
  @property({
    type: Object,
    attribute: "sign-out",
    reflect: true,
  })
  signOut?: ApplicationItemInterface = undefined;

  /**
   * Sign In url and title
   */
  @property({
    type: Object,
    attribute: "sign-in",
    reflect: true,
  })
  signIn?: ApplicationItemInterface = undefined;

  /**
   * Organizations
   */
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  organizations: Array<ApplicationItemInterface> = [];

  /**
   * Language switch URLs
   */
  @property({
    type: Array,
    attribute: "language-switch",
    reflect: true,
  })
  languageSwitch: Array<ApplicationItemInterface> = [];

  /**
   * Active application title
   */
  @property({
    type: String,
    attribute: "app-title",
    reflect: true,
  })
  appTitle: String = "";

  private userAvatarData?: AvatarInterface;

  @property({ type: Object, attribute: "cart" })
  cart?: CartInterface;

  @state()
  loaded: boolean = false;

  /**
   * Open sidebar icon
   */
  @query(".activator-holder")
  activatorElement!: HTMLElement;

  onSidebarClosed: () => void = () => {};

  onItemSelected: (this: HTMLElement, e: Event) => any = () => {};

  onCartChanged: (e: Event) => any = () => {};

  private languageSwitchRef: Ref<SlhNavigationList> = createRef();

  connectedCallback() {
    super.connectedCallback();
    this.onSidebarClosed = this.sidebarClosed.bind(this);
    document.addEventListener(EVENT_SIDEBAR_CLOSED, this.onSidebarClosed);
    this.onItemSelected = this.itemSelected.bind(this);
    document.addEventListener(ITEM_CLICK, this.onItemSelected);
    this.onCartChanged = this.cartChanged.bind(this);
    document.addEventListener(CART_CHANGED_EVENT, this.onCartChanged);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT_SIDEBAR_CLOSED, this.onSidebarClosed);
    document.removeEventListener(ITEM_CLICK, this.onItemSelected);
    document.removeEventListener(CART_CHANGED_EVENT, this.onCartChanged);
    super.disconnectedCallback();
  }

  /**
   * Derives `loaded` flag and `userAvatarData` before render so they are
   * available immediately in the template without an extra render cycle.
   */
  protected willUpdate(changedProperties: PropertyValues) {
    if (
      changedProperties.has("user") ||
      changedProperties.has("signIn") ||
      changedProperties.has("signOut") ||
      changedProperties.has("userApplications")
    ) {
      this.loaded = true;
    }
    if (changedProperties.has("user")) {
      this.userAvatarData = { fullName: this.user?.fullName || "" };
    }
  }

  protected firstUpdated() {
    this.settings?.forEach((item: ApplicationItemInterface) => {
      if (item.active) {
        this.setActiveSettingsItem(item.identifier);
      }
    });
  }

  /**
   * Renders admin application with provided identifier as active
   */
  public setActiveSettingsItem(identifier: string | null): void {
    this.settings = this.settings.map((item) => ({
      ...item,
      active: item.identifier === identifier,
    }));
  }

  /**
   * Renders user application with provided identifier as active
   */
  public setActiveUserApplication(identifier: string): void {
    this.userApplications = this.userApplications.map((item) => ({
      ...item,
      active: item.identifier === identifier,
    }));
  }

  /**
   * Renders language switch URLs with provided identifier as active
   */
  public setActiveLanguageSwitchApplication(identifier: string): void {
    this.languageSwitch = this.languageSwitch.map((item) => ({
      ...item,
      active: item.identifier === identifier,
    }));
  }

  public getActiveLanguageSwitchApplication(): ApplicationItemInterface | null {
    let activeItem: ApplicationItemInterface | null = null;
    this.languageSwitch?.forEach((item) => {
      if (activeItem === null) {
        activeItem = item;
      }
      if (item.active) {
        activeItem = item;
      }
    });
    return activeItem;
  }

  /**
   * Appends user application items
   */
  addUserApplications(items: Array<ApplicationItemInterface>) {
    if (items && items.length > 0) {
      this.userApplications = this.userApplications?.concat(items);
    }
  }

  /**
   * @returns render html template
   */
  render(): TemplateResult {
    if (!this.loaded) {
      return this.renderSkeleton();
    }
    return html`
      <div class="slh-nav-headerbar ${classMap(this.renderClasses())}">
        ${this.renderHeaderbarItems()}
      </div>
    `;
  }

  renderHeaderbarItems(): TemplateResult {
    if (this.loggedIn()) {
      return html` <div class="flex-holder">
          ${this.renderSidebarActivator()} ${this.renderAppTitle()}
        </div>
        <div class="flex-holder">
          ${this.renderLanguageSwitchItems()} ${this.renderCartItems()}
          ${this.renderSettingsItems()} ${this.renderUserItems()}
        </div>`;
    }
    return this.hasGuestUserMenu()
      ? this.renderGuestUserItems()
      : this.renderSignInLink();
  }

  renderSkeleton(): TemplateResult {
    return html`<div class="skeleton-wrapper">
      <div class="skeleton-circle"></div>
      <div class="skeleton-actions">
        <div class="skeleton-circle"></div>
        <div class="skeleton-circle"></div>
      </div>
    </div>`;
  }

  renderSidebarActivator(): TemplateResult {
    return html`<div
      class="activator-holder"
      tabindex="0"
      @keydown="${this.handleKeydown}"
      @click="${this.clickEvent}"
    >
      <slh-icon type="${IconDefaultType.ICO_HUB_NAV}"></slh-icon>
    </div>`;
  }

  renderAppTitle(): TemplateResult {
    return this.appTitle !== ""
      ? html`<div class="app-title-holder" tabindex="0">${this.appTitle}</div>`
      : html``;
  }

  private formatMoney(amount: number): string {
    return new Intl.NumberFormat(this.getLocale(), {
      minimumFractionDigits: DECIMAL_PLACES,
      maximumFractionDigits: DECIMAL_PLACES,
    }).format(amount);
  }

  renderCartItems(): TemplateResult | typeof nothing {
    if (!this.cart?.enabled || !this.cart?.items?.length) {
      return nothing;
    }
    return html`
      <slh-nav-list
        aria-label=""
        class="cart-items"
        offset="${NAVIGATION_LIST_OFFSET}"
      >
        <div slot="activator" tabindex="-1" class="activator-holder">
          <slh-icon type="${IconDefaultType.ICO_CART}"></slh-icon>
        </div>
        <div class="cart-popup">
          <div class="cart-popup-header">
            <span class="cart-popup-title"
              >${this.trans("headerbar.cart.title")}</span
            >
          </div>
          <hr class="nav-list-user-separator" />
          <div class="cart-popup-items">
            ${this.cart.items.map(
              (item) => html`
                <div class="cart-popup-item">
                  <span class="cart-popup-item-title">${item.title}</span>
                  <span class="cart-popup-item-price"
                    >${this.cart!.currency}
                    ${this.formatMoney(item.price)}</span
                  >
                </div>
              `,
            )}
          </div>
          <hr class="nav-list-user-separator" />
          <div class="cart-popup-total cart-popup-summary">
            <span class="cart-popup-total-label"
              >${this.trans("headerbar.cart.subtotal")}</span
            >
            <span class="cart-popup-total-amount"
              >${this.cart.currency}
              ${this.formatMoney(this.cart.subtotal)}</span
            >
          </div>
          ${(this.cart.vatLines ?? [])
            .filter((line) => line.rate > 0)
            .map(
              (line) => html`
                <div class="cart-popup-total cart-popup-summary">
                  <span class="cart-popup-total-label"
                    >+ ${this.trans("headerbar.cart.vat")} (${line.rate}%)</span
                  >
                  <span class="cart-popup-total-amount"
                    >${this.cart!.currency}
                    ${this.formatMoney(line.amount)}</span
                  >
                </div>
              `,
            )}
          <hr class="nav-list-user-separator" />
          <div class="cart-popup-total">
            <span class="cart-popup-total-label"
              >${this.trans("headerbar.cart.total")}</span
            >
            <span class="cart-popup-total-amount"
              >${this.cart.currency} ${this.formatMoney(this.cart.total)}</span
            >
          </div>
          <hr class="nav-list-user-separator" />
          <slh-nav-item
            class="cart-popup-link"
            href="${this.cart.cartUrl}"
            type="${SlhNavigationItemTypeEnum.CART}"
            ><slh-icon
              slot="icon"
              type="${IconDefaultType.ICO_DOUBLEARROW}"
              family="${ICON_FAMILY_DEFAULT}"
            ></slh-icon
            ><span
              >${this.trans("headerbar.cart.go_to_cart")}</span
            ></slh-nav-item
          >
        </div>
      </slh-nav-list>
    `;
  }

  renderSettingsItems(): TemplateResult | typeof nothing {
    if (this.settings?.length <= 0) {
      return nothing;
    }
    return html`
      <slh-nav-list
        aria-label=""
        class="settings-items"
        offset="${NAVIGATION_LIST_OFFSET}"
      >
        <div slot="activator" tabindex="-1" class="activator-holder">
          <slh-icon type="${IconDefaultType.ICO_PREFERENCES}"></slh-icon>
        </div>
        ${repeat(
          this.settings,
          (item) => item.identifier,
          (item) => this.renderSettingsItem(item),
        )}
      </slh-nav-list>
    `;
  }

  renderLanguageSwitchItems(): TemplateResult | typeof nothing {
    if (this.languageSwitch?.length <= 0) {
      return nothing;
    }
    return html`
      <slh-nav-list
        aria-label=""
        class="settings-items language-switch-items"
        offset="${NAVIGATION_LIST_OFFSET}"
        ${ref(this.languageSwitchRef)}
      >
        <div
          slot="activator"
          tabindex="-1"
          class="activator-holder language-switch"
        >
          ${this.getActiveLanguageSwitchApplication()?.identifier?.substring(
            0,
            2,
          )}
        </div>
        ${repeat(
          this.languageSwitch,
          (item) => item.identifier,
          (item) => this.renderLanguageSwitchItem(item),
        )}
      </slh-nav-list>
    `;
  }

  renderUserItems(): TemplateResult {
    if (this.userApplications?.length <= 0 && this.signOut === undefined) {
      return this.renderAvatar();
    }
    return html`
      <slh-nav-list aria-label="" offset="${NAVIGATION_LIST_OFFSET}">
        <div slot="activator">${this.renderAvatar()}</div>
        <div class="nav-list-profile">
          ${this.renderAvatar(Sizes.SM)}
          <div class="user-profile-data">
            <div class="user-profile-name">${this.user?.fullName}</div>
            <div class="user-profile-username">${this.user?.username}</div>
          </div>
        </div>
        ${this.organizations.length > 0
          ? html`<hr class="nav-list-user-separator" />`
          : html``}
        ${repeat(
          this.organizations,
          (item) => item.identifier,
          (item, index) => this.renderOrganizationItem(item, index),
        )}
        ${this.organizations.length > ORGANIZATION_VISIBLE_ITEMS
          ? html`<div class="organization-collapse view-all">
              ${this.trans("headerbar.organization.view_all")}
            </div>`
          : html``}
        ${repeat(
          this.userApplications,
          (item) => item.identifier,
          (item) => this.renderUserApplicationItem(item),
        )}
        ${this.renderSignOutItem()}
      </slh-nav-list>
    `;
  }

  /**
   * User menu for non-authenticated users.
   * Same as the authenticated user menu, without user profile and organization items.
   */
  renderGuestUserItems(): TemplateResult {
    return html`
      <slh-nav-list
        aria-label="${this.trans("headerbar.user_menu")}"
        class="guest-user-items"
        offset="${NAVIGATION_LIST_OFFSET}"
      >
        <div slot="activator" tabindex="-1" class="activator-holder">
          <slh-icon type="${IconDefaultType.ICO_CLIENT}"></slh-icon>
        </div>
        ${repeat(
          this.userApplications,
          (item) => item.identifier,
          (item) => this.renderUserApplicationItem(item, false),
        )}
        ${this.renderSignInItem()}
      </slh-nav-list>
    `;
  }

  renderSettingsItem(item: ApplicationItemInterface): TemplateResult {
    return html`<slh-nav-item
      ?active="${item.active}"
      href="${item.url}"
      ?prevent-default="${item.preventDefault || false}"
      type="${SlhNavigationItemTypeEnum.SETTING}"
      ><div class="settings-item">${item.title}</div></slh-nav-item
    >`;
  }

  renderLanguageSwitchItem(item: ApplicationItemInterface): TemplateResult {
    return html`<slh-nav-item
      ?active="${item.active}"
      href="${item.url}"
      id="${item.identifier}"
      ?prevent-default="${item.preventDefault || false}"
      type="${SlhNavigationItemTypeEnum.LANGUAGE}"
      ><div class="settings-item language-switch-item">
        ${item.title}
      </div></slh-nav-item
    >`;
  }

  renderSignOutItem(): TemplateResult {
    return html`<hr class="nav-list-user-separator" />
      <slh-nav-item
        href="${this.signOut?.url}"
        ?prevent-default="${this.signOut?.preventDefault || false}"
        type="${SlhNavigationItemTypeEnum.SIGNOUT}"
        >${this.signOut?.icon !== undefined && this.signOut?.icon !== ""
          ? html`<slh-icon
              slot="icon"
              type="${this.signOut?.icon}"
              family="${ICON_FAMILY_NAVIGATION}"
            ></slh-icon>`
          : html``}<span class="user-item-label"
          >${this.signOut?.title
            ? this.signOut.title
            : this.trans("headerbar.sign_out")}</span
        ></slh-nav-item
      >`;
  }

  /**
   * Sign in item of the guest user menu.
   * Omitted when no sign in url is provided.
   */
  renderSignInItem(): TemplateResult | typeof nothing {
    if (!this.signIn?.url) {
      return nothing;
    }
    return html`<hr class="nav-list-user-separator" />
      <slh-nav-item
        href="${this.signIn?.url}"
        ?prevent-default="${this.signIn?.preventDefault || false}"
        type="${SlhNavigationItemTypeEnum.SIGNIN}"
        ><slh-icon
          slot="icon"
          type="${this.signIn?.icon || SIGN_IN_ICON}"
          family="${ICON_FAMILY_NAVIGATION}"
        ></slh-icon
        ><span class="user-item-label"
          >${this.signIn?.title
            ? this.signIn.title
            : this.trans("headerbar.sign_in")}</span
        ></slh-nav-item
      >`;
  }

  renderUserApplicationItem(
    item: ApplicationItemInterface,
    separated: boolean = true,
  ): TemplateResult {
    return html`${this.renderSeparator(item, separated)}
      <slh-nav-item
        href="${item.url}"
        ?active="${item.active}"
        ?prevent-default="${item.preventDefault || false}"
        type="${SlhNavigationItemTypeEnum.USER}"
        >${item.icon !== undefined && item.icon !== ""
          ? html`<slh-icon
              slot="icon"
              type="${item.icon}"
              family="${ICON_FAMILY_NAVIGATION}"
            ></slh-icon>`
          : html``}<span class="user-item-label"
          >${item.title}</span
        ></slh-nav-item
      >`;
  }

  renderOrganizationItem(
    item: ApplicationItemInterface,
    index: number,
  ): TemplateResult {
    return html` <slh-nav-item
      class="organization ${index > ORGANIZATION_VISIBLE_ITEMS - 1
        ? "hidden-item hide"
        : ""}"
      href="${item.url}"
      ?active="${item.active}"
      ?prevent-default="${item.active}"
      organization="true"
      type="${SlhNavigationItemTypeEnum.ORGANIZATION}"
    >
      <slh-nav-organization-avatar
        slot="icon"
        size="${Sizes.SM}"
        .user=${item.title
          ? this.organizationAvatarUser(item.title, index)
          : {}}
      ></slh-nav-organization-avatar>
      <div class="organization-content">
        <span
          >${item.title && item.title.length > ORGANIZATION_TITLE_MAX_CHARACTER
            ? `${item.title.slice(0, ORGANIZATION_TITLE_MAX_CHARACTER)}...`
            : item.title}</span
        >
        ${item.active
          ? html`<slh-icon type="${ACTIVE_ORGANIZATION_ICON}"></slh-icon>`
          : html``}
      </div>
    </slh-nav-item>`;
  }

  renderSeparator(
    item: ApplicationItemInterface,
    separated: boolean = true,
  ): TemplateResult {
    const index = this.userApplications.indexOf(item);
    if (separated && index === 0) {
      return html`<hr class="nav-list-user-separator" />`;
    }
    return html``;
  }

  renderSignInLink(): TemplateResult {
    if (this.signIn?.url !== "" && this.signIn?.url !== undefined) {
      return html` <a href="${this.signIn?.url}" class="slh-nav-link"
        >${this.signIn?.title
          ? this.signIn.title
          : this.trans("headerbar.sign_in")}</a
      >`;
    }
    return html`${nothing}`;
  }

  /**
   * @param {KeyboardEvent} event
   * w3.org keyboard support
   */
  protected handleKeydown(event: KeyboardEvent) {
    const { code } = event;
    if (A11Y_OPEN_SIDEBAR_KEYCODES.includes(code)) {
      this.clickEvent();
    }
  }

  protected sidebarClosed() {
    this.activatorElement?.focus();
  }

  protected itemSelected(event: Event) {
    if (
      event instanceof CustomEvent &&
      event.detail.type === SlhNavigationItemTypeEnum.LANGUAGE &&
      event.detail?.id !== undefined
    ) {
      this.setActiveLanguageSwitchApplication(event.detail.id);

      this.languageSwitchRef?.value!.close();
      (this.languageSwitchRef?.value! as HTMLElement).blur();
    }
  }

  private cartChanged(event: Event): void {
    if (event instanceof CustomEvent) {
      this.cart = event.detail?.cart;
    }
  }

  private renderAvatar(size: Sizes = Sizes.MD): TemplateResult {
    return html`
      <slh-nav-avatar
        tabindex="-1"
        size="${size}"
        .user=${this.userAvatarData}
        src="${this.user?.image}"
      ></slh-nav-avatar>
    `;
  }

  /**
   * @fires "slhuinav:sidebar-toggle"
   */
  private clickEvent() {
    const customEvent = new CustomEvent(SIDEBAR_ACTIVATOR_EVENT_CLICK, {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  }

  /**
   * @returns {ClassInfo}
   */
  private renderClasses(): ClassInfo {
    return {
      "no-auth": this.loaded && !this.loggedIn(),
      auth: this.loaded && this.loggedIn(),
    };
  }

  /**
   * Guest user menu replaces the sign-in link when user application items are provided.
   */
  private hasGuestUserMenu(): boolean {
    return this.userApplications?.length > 0;
  }

  private loggedIn(): boolean {
    return (
      this.user !== null &&
      this.user !== undefined &&
      JSON.stringify(this.user) !== "{}"
    );
  }

  private organizationAvatarUser(title: string, index: number): UserType {
    return { fullName: title + index };
  }
}
